import { useEffect, useState } from 'react';

/**
 * `true` while the tab is in front.
 *
 * Every 30-second poll in the admin portal is multiplied by it: a background
 * tab that keeps re-reading the open punch sessions burns the supervisor's
 * battery and the API's throttle budget for a screen nobody is looking at.
 * RTK Query treats a `pollingInterval` of `0` as "do not poll", so the call
 * site reads `pollingInterval: visible ? 30_000 : 0`.
 *
 * It answers `true` where there is no `document` (SSR, a worker) rather than
 * `false`: a screen that renders once and never polls because it guessed
 * "hidden" is the worse failure of the two.
 */
export function useDocumentVisible(): boolean {
  const [visible, setVisible] = useState(
    () => typeof document === 'undefined' || document.visibilityState !== 'hidden',
  );

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const update = (): void => setVisible(document.visibilityState !== 'hidden');
    // The state can have changed between the lazy initializer and the effect.
    update();
    document.addEventListener('visibilitychange', update);
    return () => document.removeEventListener('visibilitychange', update);
  }, []);

  return visible;
}
